import { useState } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Repeat, Download, ChevronDown } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn, formatVND } from '@/lib/utils';
import { saveTemplate, deleteTemplate, type TemplateInput } from '../../../ledger/plans';
import { duplicateTemplateIds, yearlyOutlook, followsLast } from '../../../finance';
import { useData } from '../../store/data';
import { confirmAsk } from '../../components/confirm';
import { FormSheet, Field, MoneyField, WalletSelect } from '../../components/FormSheet';
import { parseAmount } from '../entry/formSchema';
import { paymentCap } from './paymentCap';
import Money from '../../components/kit/Money';
import { fmtMoney } from '../../components/kit/format';
import { KIND } from './DayList';
import { groupTemplates } from './recurringView';
import ImportTemplatesSheet, { useImportCandidates, CONFIDENT } from './ImportTemplatesSheet';

const when = (t: any) => (t.frequency === 'yearly' ? `Ngày ${t.dayOfMonth || 1} tháng ${t.monthOfYear || 1} hằng năm` : `Ngày ${t.dayOfMonth || 1} hằng tháng`);

/** Bật: mỗi kỳ, số dự kiến lấy theo khoản đã xác nhận của kỳ trước (kỳ trước chưa xác nhận thì dùng số của mẫu). */
function FollowToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start gap-2 rounded-md bg-muted px-3 py-2 text-sm">
      <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span><b>Tự lấy số của kỳ trước.</b> Khi tạo khoản kỳ này, dùng số tiền khoản kỳ trước đã xác nhận. Kỳ trước chưa xác nhận thì dùng số ghi trong mẫu.</span>
    </label>
  );
}

/** Mẫu trả thẻ/nợ: đổi được số tiền, ngày, ví trả và thẻ/khoản vay đích (loại thẻ ↔ nợ cố định; muốn đổi loại thì tạo mẫu mới). */
function PaymentTemplateSheet({ tpl, onClose }: { tpl: any; onClose: () => void }) {
  const { sources, debts } = useData(s => s.data);
  const isCredit = tpl.paymentType === 'credit';
  const [amount, setAmount] = useState(String(tpl.amount));
  const [day, setDay] = useState(String(tpl.dayOfMonth ?? 1));
  const [walletId, setWalletId] = useState<string>(tpl.walletId ?? '');
  const [target, setTarget] = useState<string>((isCredit ? tpl.creditCardId : tpl.debtId) ?? '');
  const [follow, setFollow] = useState(followsLast(tpl));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wallets = sources.filter(s => s.status !== 'inactive' && s.type !== 'credit');
  const targets: Array<[string, string]> = isCredit
    ? sources.filter(s => s.type === 'credit' && s.status !== 'inactive').map(c => [c.id, c.name])
    : debts.filter((x: any) => x.type !== 'credit' && x.status !== 'paid').map((x: any) => [x.id, x.lender]);
  // giữ lại đích hiện tại dù thẻ/khoản vay đó đã đóng, để không mất lựa chọn đang có
  const curName = (isCredit ? tpl.creditCardName : tpl.debtName) ?? '';
  const options = target && !targets.some(([id]) => id === target) ? [[target, curName || target] as [string, string], ...targets] : targets;
  const submit = async () => {
    if (parseAmount(amount) <= 0) { setError('Nhập số tiền lớn hơn 0'); return; }
    if (!(Number(day) >= 1 && Number(day) <= 31)) { setError('Ngày từ 1 đến 31'); return; }
    if (!walletId) { setError('Chọn ví trả'); return; }
    if (!target) { setError(isCredit ? 'Chọn thẻ' : 'Chọn khoản vay'); return; }
    const targetName = options.find(([id]) => id === target)?.[1] ?? curName;
    const label = isCredit ? 'Trả thẻ' : 'Trả nợ';
    // tên tự đặt theo mẫu "Trả thẻ/nợ <đích>" thì đổi theo đích mới; tên người dùng tự đặt thì giữ nguyên
    const name = target !== (isCredit ? tpl.creditCardId : tpl.debtId) && tpl.name === `${label} ${curName}`.trim() ? `${label} ${targetName}`.trim() : tpl.name;
    setBusy(true); setError(null);
    const r = await saveTemplate(getAuth().currentUser!.uid, tpl.id, {
      ...tpl, name, amount: parseAmount(amount), dayOfMonth: Number(day), walletId, walletName: wallets.find(w => w.id === walletId)?.name ?? tpl.walletName, followLastActual: follow,
      ...(isCredit ? { creditCardId: target, creditCardName: targetName } : { debtId: target, debtName: targetName })
    });
    setBusy(false);
    if (!r.success) { setError(r.error ?? 'Không lưu được'); return; }
    toast.success('Đã cập nhật'); onClose();
  };
  return (
    <FormSheet open onClose={onClose} title={`Sửa: ${tpl.name}`} onSubmit={submit} submitLabel="Lưu" busy={busy} error={error}
      description="Số tiền chỉ là dự kiến — khi xác nhận vẫn nhập được số thực trả. Thay đổi áp dụng từ khoản được tạo sau; khoản đã tạo giữ nguyên.">
      <Field label={isCredit ? 'Thẻ tín dụng' : 'Khoản vay'}>
        <Select value={target} onValueChange={setTarget}>
          <SelectTrigger><SelectValue placeholder={isCredit ? 'Chọn thẻ' : 'Chọn khoản vay'} /></SelectTrigger>
          <SelectContent>{options.map(([id, n]) => <SelectItem key={id} value={id}>{n}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <WalletSelect label="Trả từ ví" value={walletId} onChange={setWalletId} wallets={wallets} />
      <MoneyField label="Số tiền dự kiến" value={amount} onChange={setAmount} />
      <Field label="Ngày trong tháng"><Input value={day} onChange={e => setDay(e.target.value)} inputMode="numeric" className="num" /></Field>
      <FollowToggle checked={follow} onChange={setFollow} />
    </FormSheet>
  );
}

function TemplateSheet({ tpl, onClose }: { tpl: any | null; onClose: () => void }) {
  const { sources, expenseCategories, incomeCategories } = useData(s => s.data);
  const [type, setType] = useState<'expense' | 'income'>(tpl?.type ?? 'expense');
  const [name, setName] = useState(tpl?.name ?? '');
  const [amount, setAmount] = useState(tpl ? String(tpl.amount) : '');
  const [category, setCategory] = useState(tpl?.category ?? '');
  const [walletId, setWalletId] = useState(tpl?.walletId ?? '');
  const [frequency, setFrequency] = useState<'monthly' | 'yearly'>(tpl?.frequency ?? 'monthly');
  const [follow, setFollow] = useState<boolean>(tpl ? followsLast(tpl) : false);
  const [day, setDay] = useState(String(tpl?.dayOfMonth ?? 1));
  const [moy, setMoy] = useState(String(tpl?.monthOfYear ?? 1));
  const [err, setErr] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const cats = [...new Set<string>((type === 'income' ? incomeCategories : expenseCategories).map((c: any) => c.name))];
  const wallets = sources.filter(s => s.status !== 'inactive' && (type === 'income' ? s.type !== 'credit' : true));

  const submit = async () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Nhập tên khoản';
    if (parseAmount(amount) <= 0) e.amount = 'Nhập số tiền lớn hơn 0';
    if (!category) e.category = 'Chọn danh mục';
    if (!walletId) e.walletId = 'Chọn ví';
    if (!(Number(day) >= 1 && Number(day) <= 31)) e.day = 'Ngày từ 1 đến 31';
    setErr(e);
    if (Object.keys(e).length) return;
    setBusy(true); setServerError(null);
    // Mẫu mới: nếu ngày trong tháng đã qua thì bắt đầu từ tháng sau (tránh sinh ngay khoản quá hạn trùng với khoản đã tự nhập)
    const nowD = new Date();
    const startMonth = tpl ? undefined : (() => {
      const d = frequency === 'yearly' ? 0 : Number(day);
      const m = frequency === 'monthly' && d >= nowD.getDate() ? nowD : new Date(nowD.getFullYear(), nowD.getMonth() + 1, 1);
      return `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
    })();
    const input: TemplateInput = {
      type, name, ...(startMonth ? { startMonth } : {}), amount: parseAmount(amount), category, walletId, walletName: wallets.find(w => w.id === walletId)?.name,
      frequency, dayOfMonth: Number(day), monthOfYear: Number(moy), active: tpl?.active ?? true, followLastActual: follow
    };
    const r = await saveTemplate(getAuth().currentUser!.uid, tpl?.id ?? null, input);
    setBusy(false);
    if (!r.success) { setServerError(r.error ?? 'Không lưu được'); return; }
    toast.success(tpl ? 'Đã cập nhật khoản định kỳ' : 'Đã thêm khoản định kỳ'); onClose();
  };

  return (
    <FormSheet open onClose={onClose} title={tpl ? 'Sửa khoản định kỳ' : 'Thêm khoản định kỳ'} onSubmit={submit} submitLabel="Lưu" busy={busy} error={serverError}
      description="Mỗi kỳ, khoản này xuất hiện ở Dự kiến khi bạn mở app. Bạn bấm xác nhận để ghi sổ — app không tự trừ tiền.">
      {!tpl && (
        <Tabs value={type} onValueChange={v => { setType(v as any); setCategory(''); setWalletId(''); }}>
          <TabsList className="grid w-full grid-cols-2"><TabsTrigger value="expense">Chi</TabsTrigger><TabsTrigger value="income">Thu</TabsTrigger></TabsList>
        </Tabs>
      )}
      <Field label="Tên" error={err.name}><Input value={name} onChange={e => setName(e.target.value)} placeholder={type === 'income' ? 'vd: Lương công ty' : 'vd: Tiền nhà'} aria-invalid={!!err.name} /></Field>
      <MoneyField label="Số tiền" value={amount} onChange={setAmount} error={err.amount} />
      <Field label="Danh mục" error={err.category}>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger aria-invalid={!!err.category}><SelectValue placeholder="Chọn danh mục" /></SelectTrigger>
          <SelectContent>{(category && !cats.includes(category) ? [category, ...cats] : cats).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <WalletSelect label={type === 'income' ? 'Ví nhận' : 'Ví / thẻ thanh toán'} value={walletId} onChange={setWalletId} wallets={wallets} error={err.walletId} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Lặp lại">
          <Select value={frequency} onValueChange={v => setFrequency(v as any)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="monthly">Hằng tháng</SelectItem><SelectItem value="yearly">Hằng năm</SelectItem></SelectContent>
          </Select>
        </Field>
        <Field label="Ngày trong tháng" error={err.day}><Input value={day} onChange={e => setDay(e.target.value)} inputMode="numeric" className="num" aria-invalid={!!err.day} /></Field>
      </div>
      {frequency === 'yearly' && <Field label="Tháng trong năm"><Input value={moy} onChange={e => setMoy(e.target.value)} inputMode="numeric" className="num" /></Field>}
      <FollowToggle checked={follow} onChange={setFollow} />
    </FormSheet>
  );
}

/** Một mẫu: biểu tượng loại, tên, lịch + danh mục + ví ở dòng phụ, số tiền, công tắc bật/tắt, sửa, xoá. */
function TemplateRow({ t, sources, debts, onToggle, onEdit, onRemove }: { t: any; sources: any[]; debts: any[]; onToggle: (t: any) => void; onEdit: (t: any) => void; onRemove: (t: any) => void }) {
  const k = KIND[t.type as keyof typeof KIND] ?? KIND.expense;
  const now = new Date();
  const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const future = t.startMonth && t.startMonth > nowKey ? ` · bắt đầu ${t.startMonth.slice(5)}/${t.startMonth.slice(0, 4)}` : '';
  const sub = `${when(t)}${t.type === 'payment' ? ` · từ ${t.walletName || 'ví'}` : ` · ${t.category || 'Chưa có danh mục'} · ${t.walletName || 'ví'}`}${future}${followsLast(t) ? ' · tự theo số kỳ trước' : ''}`;
  const cap = t.type === 'payment' && t.active ? paymentCap(t, sources, debts) : null;
  const over = cap !== null && Number(t.amount) > cap;
  return (
    <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3', !t.active && 'opacity-60')}>
      <span role="img" aria-label={k.label} className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full', k.cls)}><k.Icon size={17} aria-hidden /></span>
      <div className="min-w-0 flex-1 basis-44">
        <p className="truncate text-base font-medium">{t.name}</p>
        <p className="truncate text-sm text-muted-foreground">{sub}</p>
        {over && <p className="text-sm text-warn">{cap === 0 ? 'Thẻ/khoản vay này hiện không còn nợ — nên tắt hoặc xoá mẫu.' : `Mẫu lớn hơn số còn nợ (${formatVND(cap!)}) — khi xác nhận sẽ điền sẵn số còn nợ.`}</p>}
      </div>
      <Money value={t.type === 'income' ? Number(t.amount) || 0 : -(Number(t.amount) || 0)} plus={t.type === 'income'} tone={t.type === 'income' ? 'in' : undefined} />
      <div className="flex items-center gap-1">
        <button type="button" role="switch" aria-checked={!!t.active} aria-label={`${t.active ? 'Tắt' : 'Bật'} ${t.name}`} onClick={() => onToggle(t)}
          className={cn('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-transparent transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary', t.active ? 'bg-primary' : 'bg-muted')}>
          <span className={cn('inline-block h-5 w-5 rounded-full bg-background shadow transition-transform motion-reduce:transition-none', t.active ? 'translate-x-5' : 'translate-x-0.5')} />
        </button>
        <Button variant="ghost" size="icon" onClick={() => onEdit(t)} aria-label={`Sửa ${t.name}`}><Pencil size={15} /></Button>
        <Button variant="ghost" size="icon" onClick={() => onRemove(t)} aria-label={`Xoá ${t.name}`}><Trash2 size={15} /></Button>
      </div>
    </div>
  );
}

export default function RecurringTab() {
  const templates = useData(s => s.data.recurringTemplates);
  const { sources, debts } = useData(s => s.data);
  const [sheet, setSheet] = useState<{ tpl: any | null } | null>(null);
  const [importing, setImporting] = useState(false);
  const { cands: all } = useImportCandidates();
  const cands = all.filter(c => c.seenMonths >= CONFIDENT); // khoản mới thấy 1–2 tháng không nhắc mãi
  const view = groupTemplates(templates);
  const [showOff, setShowOff] = useState(false);
  const yearly = yearlyOutlook(templates);
  const uid = () => getAuth().currentUser!.uid;
  const dupIds = duplicateTemplateIds(templates);
  const removeDups = async () => {
    if (!await confirmAsk({ title: `Xoá ${dupIds.length} mẫu trùng?`, description: 'Giữ lại bản tạo sớm nhất của mỗi khoản. Các khoản đã sinh ra từ mẫu không bị ảnh hưởng.', confirmLabel: `Xoá ${dupIds.length} bản trùng` })) return;
    const rs = await Promise.all(dupIds.map(id => deleteTemplate(uid(), id)));
    const bad = rs.filter(r => !r.success).length;
    bad ? toast.error(`${bad} mẫu chưa xoá được`) : toast.success(`Đã xoá ${dupIds.length} mẫu trùng`);
  };

  const toggle = async (t: any) => {
    const r = await saveTemplate(uid(), t.id, { ...t, active: !t.active });
    if (!r.success) toast.error(r.error ?? 'Không cập nhật được');
  };
  const remove = async (t: any) => {
    if (!await confirmAsk({ title: `Xoá khoản định kỳ "${t.name}"?`, description: 'Các khoản đã tạo ra từ mẫu vẫn giữ nguyên.' })) return;
    const r = await deleteTemplate(uid(), t.id);
    r.success ? toast.success('Đã xoá') : toast.error(r.error ?? 'Không xoá được');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="max-w-prose text-sm text-muted-foreground">Khoản lặp lại đều đặn (tiền nhà, lương, bảo hiểm…). Khi bạn mở app, khoản của tháng này tự xuất hiện ở <b>Dự kiến</b> để bạn xác nhận khi đến ngày.</p>
        <Button size="sm" className="shrink-0" onClick={() => setSheet({ tpl: null })}><Plus size={14} aria-hidden /> Thêm</Button>
      </div>
      {dupIds.length > 0 && (
        <Card role="alert" className="flex flex-wrap items-center justify-between gap-3 border-warn/40 bg-warn-soft p-4">
          <div className="min-w-0 flex-1 basis-60">
            <p className="text-sm font-medium text-warn">{dupIds.length} mẫu bị trùng</p>
            <p className="text-xs text-muted-foreground">Mẫu trùng sẽ sinh hai khoản mỗi tháng. Xoá các bản thừa, giữ bản tạo sớm nhất.</p>
          </div>
          <Button variant="outline" onClick={removeDups}><Trash2 size={14} aria-hidden /> Xoá {dupIds.length} bản trùng</Button>
        </Card>
      )}
      {cands.length > 0 && (
        <Card className="flex flex-wrap items-center justify-between gap-3 border-primary/40 bg-accent/40 p-4">
          <div className="min-w-0 flex-1 basis-60">
            <p className="text-sm font-medium">{cands.length} khoản định kỳ từ bản cũ chưa có mẫu</p>
            <p className="text-xs text-muted-foreground">Không có mẫu thì sang tháng mới các khoản này sẽ không tự xuất hiện ở Dự kiến. Xem lại và nhận một lần.</p>
          </div>
          <Button onClick={() => setImporting(true)}><Download size={14} aria-hidden /> Nhập từ dữ liệu cũ</Button>
        </Card>
      )}
      {yearly.length > 0 && (
        <Card className="space-y-2 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-medium">Khoản đóng theo năm</p>
            <p className="num text-xs text-muted-foreground">Nên để dành {formatVND(yearly.reduce((s, y) => s + y.perMonth, 0))}/tháng cho cả nhóm</p>
          </div>
          <ul className="num space-y-1 text-sm">
            {yearly.map(y => (
              <li key={y.id} className="flex flex-wrap justify-between gap-x-3">
                <span className="min-w-0 truncate">{y.name} · {formatVND(y.amount)}</span>
                <span className={cn('shrink-0', y.daysLeft <= 30 ? 'font-medium text-warn' : 'text-muted-foreground')}>{y.next.toLocaleDateString('vi-VN')} · {y.daysLeft === 0 ? 'hôm nay' : `còn ${y.daysLeft} ngày`} · để dành {formatVND(y.perMonth)}/tháng</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
      {templates.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground"><Repeat size={20} aria-hidden />Chưa có khoản định kỳ nào.</Card>
      ) : (
        <>
          <Card className="space-y-3 p-4">
            <p className="text-sm font-medium">Mỗi tháng theo các mẫu đang bật</p>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
              {[
                { l: 'Thu', v: view.income, tone: 'in' as const }, { l: 'Chi', v: -view.expense, tone: undefined },
                { l: 'Trả thẻ & nợ', v: -view.payment, tone: undefined }, { l: 'Ròng', v: view.net, tone: undefined }
              ].map(x => <div key={x.l}><dt className="text-sm text-muted-foreground">{x.l}</dt><dd><Money value={x.v} size="figure" tone={x.tone} compact /></dd></div>)}
            </dl>
            <p className="text-xs text-muted-foreground">Chưa gồm thưởng quý, học phí và các khoản ngoài mẫu. Khoản hằng năm tính riêng ở phần "Hằng năm".</p>
          </Card>
          {view.sections.map(sec => {
            const body = (
              <Card className="divide-y divide-border overflow-hidden p-0">
                {sec.items.map((t: any) => <TemplateRow key={t.id} t={t} sources={sources} debts={debts} onToggle={toggle} onEdit={t => setSheet({ tpl: t })} onRemove={remove} />)}
              </Card>
            );
            return (
              <section key={sec.id} className="space-y-2" aria-label={sec.label}>
                {sec.id === 'off' ? (
                  <>
                    <button onClick={() => setShowOff(o => !o)} aria-expanded={showOff} className="flex min-h-9 items-center gap-1 px-1 text-base font-semibold hover:text-primary">
                      <ChevronDown size={16} className={cn('transition-transform motion-reduce:transition-none', !showOff && '-rotate-90')} aria-hidden /> {sec.label} <span className="num font-normal text-muted-foreground">{sec.items.length}</span>
                    </button>
                    {showOff && body}
                  </>
                ) : (
                  <>
                    <h2 className="flex flex-wrap items-baseline gap-x-2 px-1 text-base font-semibold">{sec.label}
                      <span className="num text-sm font-normal text-muted-foreground">{sec.items.length} mẫu{sec.total > 0 ? ` · ${fmtMoney(sec.total, { compact: true })}${sec.id === 'yearly' ? '/năm' : '/tháng'}` : ''}</span>
                    </h2>
                    {body}
                  </>
                )}
              </section>
            );
          })}
        </>
      )}
      {sheet && (sheet.tpl?.type === 'payment' ? <PaymentTemplateSheet tpl={sheet.tpl} onClose={() => setSheet(null)} /> : <TemplateSheet tpl={sheet.tpl} onClose={() => setSheet(null)} />)}
      {importing && <ImportTemplatesSheet onClose={() => setImporting(false)} />}
    </div>
  );
}
